import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Building, MapPin, Phone } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../common/apiClient';
import SummaryApi from '../../../../common/SummaryApi';
import SelectDropdown from '../../../../customStyles/SelectDropdown';

const BranchForm = () => {
  const { portalName, id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [executives, setExecutives] = useState([]);

  const [formData, setFormData] = useState({
    branchName: '',
    branchCode: '',
    divisionIds: [],
    isMainBranch: false,
    branchManagerId: '',
    contactPhone: '',
    contactEmail: '',
    landline: '',
    address: '',
    locality: '',
    district: '',
    state: '',
    country: 'IN',
    pincode: '',
    gstNumber: '',
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState({});

  // Fetch Divisions and Executives
  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const [divRes, execRes] = await Promise.all([
          api.get(SummaryApi.getAllDivisions.url),
          api.get(SummaryApi.executiveUsers.url),
        ]);
        if (divRes.data?.success) setDivisions(divRes.data.data || []);
        if (execRes.data?.success) {
          const rawExecs = execRes.data.data || execRes.data.users || execRes.data || [];
          const formatted = rawExecs.map(e => ({
            _id: String(e._id || e.id || e.userId),
            name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email || 'Executive'
          }));
          setExecutives(formatted);
        }
      } catch (err) {
        console.error('Error loading references:', err);
      }
    };
    fetchReferences();
  }, []);

  // Fetch Branch details for Edit Mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchBranch = async () => {
      setLoading(true);
      try {
        const url = typeof SummaryApi.getBranchById.url === 'function'
          ? SummaryApi.getBranchById.url(id)
          : SummaryApi.getBranchById.url;

        const response = await api.get(url);
        if (response.data?.success) {
          const doc = response.data.data;
          setFormData({
            branchName: doc.branchName || '',
            branchCode: doc.branchCode || '',
            divisionIds: doc.divisionIds?.map(d => d._id || d) || [],
            isMainBranch: doc.isMainBranch ?? false,
            branchManagerId: doc.branchManagerId?._id || doc.branchManagerId || '',
            contactPhone: doc.contactPhone || '',
            contactEmail: doc.contactEmail || '',
            landline: doc.landline || '',
            address: doc.address || '',
            locality: doc.locality || '',
            district: doc.district || '',
            state: doc.state || '',
            country: doc.country || 'IN',
            pincode: doc.pincode || '',
            gstNumber: doc.gstNumber || '',
            isActive: doc.isActive ?? true,
          });
        }
      } catch (error) {
        console.error('Error loading branch:', error);
        toast.error('Failed to load branch details');
        navigate(`/${portalName}/home/settings/branches`);
      } finally {
        setLoading(false);
      }
    };

    fetchBranch();
  }, [id, isEditMode, navigate, portalName]);

  const validate = () => {
    const errs = {};
    if (!formData.branchName.trim()) errs.branchName = 'Branch name is required';
    if (!formData.branchCode.trim()) errs.branchCode = 'Branch code is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix form errors');
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isEditMode) {
        const url = typeof SummaryApi.updateBranch.url === 'function'
          ? SummaryApi.updateBranch.url(id)
          : SummaryApi.updateBranch.url;
        response = await api.put(url, formData);
      } else {
        response = await api.post(SummaryApi.createBranch.url, formData);
      }

      if (response.data?.success) {
        toast.success(isEditMode ? 'Branch updated successfully' : 'Branch created successfully');
        navigate(`/${portalName}/home/settings/branches`);
      } else {
        toast.error(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error(error.response?.data?.message || 'Failed to save branch');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading branch details...</p>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-xs border border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/${portalName}/home/settings/branches`)}
            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Branch' : 'Create New Branch'}
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Configure branch name, locations, contact info, and division mapping
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Section 1: Basic Info */}
        <div className="bg-white p-5 rounded-xl shadow-xs border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
            <Building className="w-4 h-4 text-red-600" />
            Branch Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Branch Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Madurai Main Branch"
                value={formData.branchName}
                onChange={(e) => setFormData(prev => ({ ...prev, branchName: e.target.value }))}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  formErrors.branchName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {formErrors.branchName && <p className="text-red-500 text-xs mt-1">{formErrors.branchName}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Branch Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. BR-MDU-01"
                value={formData.branchCode}
                onChange={(e) => setFormData(prev => ({ ...prev, branchCode: e.target.value.toUpperCase() }))}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 uppercase font-mono ${
                  formErrors.branchCode ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {formErrors.branchCode && <p className="text-red-500 text-xs mt-1">{formErrors.branchCode}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Branch Manager / In-Charge
              </label>
              <SelectDropdown
                options={executives}
                value={formData.branchManagerId}
                onChange={(val) => setFormData(prev => ({ ...prev, branchManagerId: val }))}
                placeholder="Select Branch Manager"
                valueKey="_id"
                labelKey="name"
                searchable={true}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                Mapped Divisions (Leave empty for All Divisions)
              </label>
              <SelectDropdown
                options={divisions}
                value={formData.divisionIds}
                onChange={(val) => setFormData(prev => ({ ...prev, divisionIds: val }))}
                placeholder="Select Divisions"
                valueKey="_id"
                labelKey="name"
                multiple={true}
                searchable={true}
              />
            </div>

            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="mainBranchToggle"
                checked={formData.isMainBranch}
                onChange={(e) => setFormData(prev => ({ ...prev, isMainBranch: e.target.checked }))}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
              />
              <label htmlFor="mainBranchToggle" className="text-sm font-semibold text-gray-800 cursor-pointer select-none">
                Mark as Head Office / Main Branch
              </label>
            </div>
          </div>
        </div>

        {/* Section 2: Contact & Tax Information */}
        <div className="bg-white p-5 rounded-xl shadow-xs border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
            <Phone className="w-4 h-4 text-red-600" />
            Contact & Tax Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Contact Phone</label>
              <input
                type="text"
                placeholder="e.g. 9876543210"
                value={formData.contactPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Contact Email</label>
              <input
                type="email"
                placeholder="e.g. madurai@company.com"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Branch GSTIN Number</label>
              <input
                type="text"
                placeholder="e.g. 33AAAAA0000A1Z5"
                value={formData.gstNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, gstNumber: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 uppercase font-mono"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Address & Location */}
        <div className="bg-white p-5 rounded-xl shadow-xs border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-600" />
            Location & Address
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Street Address</label>
              <textarea
                rows="2"
                placeholder="Complete street address..."
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">City / Locality</label>
              <input
                type="text"
                placeholder="e.g. Anna Nagar"
                value={formData.locality}
                onChange={(e) => setFormData(prev => ({ ...prev, locality: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">District</label>
              <input
                type="text"
                placeholder="e.g. Madurai"
                value={formData.district}
                onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">State</label>
              <input
                type="text"
                placeholder="e.g. Tamil Nadu"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Pincode</label>
              <input
                type="text"
                maxLength="6"
                placeholder="e.g. 625020"
                value={formData.pincode}
                onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Section 4: Status & Submit Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-xs border border-gray-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActiveToggle"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
            />
            <label htmlFor="isActiveToggle" className="text-sm font-semibold text-gray-800 cursor-pointer select-none">
              Active Branch
            </label>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(`/${portalName}/home/settings/branches`)}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-xs transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditMode ? 'Update Branch' : 'Create Branch'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BranchForm;